import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Tabs,
  Tab,
  CardMedia,
  CardContent,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Avatar,
  Stack,
  IconButton,
  TextField,
  Chip,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ChevronLeft,
  ChevronRight,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  deleteCustomScenario,
  fetchCallerNumbers,
  fetchScenarios,
  generateCustomScenario,
  saveCustomScenario,
  updateCustomScenario
} from './api';
import { MAX_REDIAL_OPTIONS, normalizeMaxRedials } from './constants';

/*
 * Key UX changes:
 * - Scenario/Voice live in a dedicated section card w/ subtitle.
 * - Carousels show EXACTLY 3 cards at a time.
 *   For scenarios, the "Add Scenario" card is treated as index 0 on page 0 only.
 *   Page 1+ shows ONLY real scenarios in groups of 3 (no add card).
 * - Users table section is visually separated and compact with fixed table layout,
 *   ellipsized text, and clear instructions.
 */

const SectionCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))'
    : 'linear-gradient(180deg, #fff, #fafafa)',
}));

const CarouselWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'center'
}));

const ItemCard = styled(Card)(({ theme }) => ({
  width: 300,
  height: 300,
  position: 'relative',
  borderRadius: 14,
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  display: 'flex',
  flexDirection: 'column',
}));

const AddScenarioCard = styled(Card)(({ theme }) => ({
  width: 300,
  height: 300,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  border: `2px dashed ${theme.palette.divider}`,
  cursor: 'pointer',
  background: theme.palette.action.hover,
}));

const Pill = ({ label }) => (
  <Chip label={label} size="small" sx={{ fontWeight: 700, opacity: 0.85 }} />
);

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
const AUTOMATIC_CALLER_VALUE = '__automatic_round_robin__';

const parsePhone = (value) => {
  try {
    return parsePhoneNumberFromString(String(value || '').trim()) || null;
  } catch {
    return null;
  }
};

const getCountryFlag = (countryCode) => {
  if (!/^[A-Z]{2}$/.test(countryCode || '')) return '';
  return countryCode
    .split('')
    .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
    .join('');
};

const getCallerCountryCode = (caller) => {
  const parsed = parsePhone(caller?.phone_number);
  if (parsed?.country) return parsed.country;
  const suppliedCountry = String(caller?.country || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(suppliedCountry) ? suppliedCountry : '';
};

const formatCallerPhone = (value) => parsePhone(value)?.formatInternational() || value || 'Unknown number';

const normalizeExtractionJson = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      return {};
    }
  }
  return {};
};

const getEligibleCallerPool = (targetPhone, callerNumbers) => {
  const target = parsePhone(targetPhone);
  if (!target) return callerNumbers;

  if (target.country) {
    const exactCountry = callerNumbers.filter((caller) => getCallerCountryCode(caller) === target.country);
    if (exactCountry.length) return exactCountry;
  }

  if (target.countryCallingCode) {
    const sameCallingCode = callerNumbers.filter(
      (caller) => parsePhone(caller?.phone_number)?.countryCallingCode === target.countryCallingCode
    );
    if (sameCallingCode.length) return sameCallingCode;
  }

  return callerNumbers;
};

const VishingStep3 = ({
  vishingScope,
  setVishingScope,
  scenariosDataa,
  setScenariosDataa,
  voicesData,
  onBack,
  onSave,
  campaignError,
  callerNumbersRefreshKey
}) => {
  const [tabIndex, setTabIndex] = useState(0); // 0 = Scenarios, 1 = Voices
  const [scenarioPage, setScenarioPage] = useState(0); // page number, not index
  const [voicePage, setVoicePage] = useState(0);
  const [scenariosData, setScenariosData] = useState(scenariosDataa || []);
  const [showModal, setShowModal] = useState(false);
  const [goal, setGoal] = useState('');
  const [scenarioData, setScenarioData] = useState(null);
  const [editingScenarioId, setEditingScenarioId] = useState(null);
  const [scenarioError, setScenarioError] = useState('');
  const [extractionJsonText, setExtractionJsonText] = useState('{}');
  const [extractionJsonError, setExtractionJsonError] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [callerNumbersLoading, setCallerNumbersLoading] = useState(true);
  const [callerNumbersError, setCallerNumbersError] = useState('');

  const PAGE_SIZE = 3;

  const syncScenarioList = useCallback((scenarios) => {
    setScenariosData(scenarios);
    setScenariosDataa?.(scenarios);
  }, [setScenariosDataa]);

  useEffect(() => {
    (async () => {
      try {
        const { scenarios } = await fetchScenarios();
        syncScenarioList(scenarios);
      } catch (err) {
        console.error('Failed to fetch scenarios', err);
      }
    })();
  }, [syncScenarioList]);

  const loadCallerNumbers = useCallback(async () => {
    setCallerNumbersLoading(true);
    setCallerNumbersError('');
    try {
      const numbers = await fetchCallerNumbers();
      setCallerNumbers(numbers);
      if (!numbers.length) {
        setCallerNumbersError('No caller numbers are currently available for this team.');
      }
    } catch (error) {
      setCallerNumbers([]);
      setCallerNumbersError(error?.message || 'Unable to load caller numbers.');
    } finally {
      setCallerNumbersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCallerNumbers();
  }, [callerNumbersRefreshKey, loadCallerNumbers]);

  useEffect(() => {
    setVishingScope((currentUsers) => {
      let changed = false;
      const normalizedUsers = currentUsers.map((user) => {
        const maxRedials = normalizeMaxRedials(user.max_redials);
        if (user.max_redials === maxRedials) return user;
        changed = true;
        return { ...user, max_redials: maxRedials };
      });
      return changed ? normalizedUsers : currentUsers;
    });
  }, [setVishingScope]);

  const assignCallerNumbers = useCallback(() => {
    if (!callerNumbers.length) return;

    setVishingScope((currentUsers) => {
      const poolPositions = new Map();
      let changed = false;
      const nextUsers = currentUsers.map((user) => {
        if (user.caller_number_manually_changed) return user;

        const pool = getEligibleCallerPool(user.phone, callerNumbers);
        if (!pool.length) return user;

        const poolKey = pool.map((caller) => caller.phone_number).join('|');
        const poolPosition = poolPositions.get(poolKey) || 0;
        const callerNumber = pool[poolPosition % pool.length].phone_number;
        poolPositions.set(poolKey, poolPosition + 1);

        if (user.caller_number === callerNumber && user.caller_number_manually_changed === false) {
          return user;
        }

        changed = true;
        return {
          ...user,
          caller_number: callerNumber,
          caller_number_manually_changed: false
        };
      });

      return changed ? nextUsers : currentUsers;
    });
  }, [callerNumbers, setVishingScope]);

  useEffect(() => {
    assignCallerNumbers();
  }, [assignCallerNumbers]);

  // --- Scenario pagination helpers
  const scenarioTotalPages = useMemo(() => {
    const n = scenariosData?.length || 0;
    if (n <= 0) return 1; // still show the add card page
    if (n <= 2) return 1; // page 0: [Add, s0, s1?]
    const remaining = n - 2; // after first page consumes 2 real scenarios
    return 1 + Math.ceil(remaining / PAGE_SIZE);
  }, [scenariosData]);

  const visibleScenarioItems = useMemo(() => {
    const n = scenariosData?.length || 0;
    if (scenarioPage === 0) {
      // Add card + up to two scenarios
      return ['__ADD__', ...scenariosData.slice(0, PAGE_SIZE - 1)];
    }
    if (n <= 2) return []; // no further pages
    const start = (scenarioPage - 1) * PAGE_SIZE + 2;
    return scenariosData.slice(start, start + PAGE_SIZE);
  }, [scenarioPage, scenariosData]);

  useEffect(() => {
    // Clamp current page if scenarios change
    setScenarioPage(p => clamp(p, 0, Math.max(0, scenarioTotalPages - 1)));
  }, [scenarioTotalPages]);

  // --- Voice pagination helpers (simple 3-per page)
  const voiceTotalPages = useMemo(() => {
    const n = voicesData?.length || 0;
    return Math.max(1, Math.ceil(n / PAGE_SIZE));
  }, [voicesData]);

  const visibleVoiceItems = useMemo(() => {
    const start = voicePage * PAGE_SIZE;
    return (voicesData || []).slice(start, start + PAGE_SIZE);
  }, [voicePage, voicesData]);

  useEffect(() => {
    setVoicePage(p => clamp(p, 0, Math.max(0, voiceTotalPages - 1)));
  }, [voiceTotalPages]);

  const handleScenarioChange = (i, e) => {
    const sid = e.target.value;
    const sel = (scenariosData || []).find(s => s.id === sid) || {};
    const updated = [...vishingScope];
    updated[i] = {
      ...updated[i],
      scenario: sid,
      scenario_name: sel.name,
      scenario_description: sel.description
    };
    setVishingScope(updated);
  };

  const handleVoiceChange = (i, e) => {
    const vid = e.target.value;
    const sel = (voicesData || []).find(v => v.id === vid) || {};
    const updated = [...vishingScope];
    updated[i] = {
      ...updated[i],
      voice: vid,
      voice_name: sel.name,
      voice_language: sel.language
    };
    setVishingScope(updated);
  };

  const handleCallerNumberChange = (index, value) => {
    setVishingScope((currentUsers) => currentUsers.map((user, userIndex) => (
      userIndex === index
        ? {
            ...user,
            caller_number: value === AUTOMATIC_CALLER_VALUE ? null : value,
            caller_number_manually_changed: true
          }
        : user
    )));
  };

  const handleMaxRedialsChange = (index, value) => {
    const maxRedials = normalizeMaxRedials(value);
    setVishingScope((currentUsers) => currentUsers.map((user, userIndex) => (
      userIndex === index ? { ...user, max_redials: maxRedials } : user
    )));
  };

  const handleCampaignSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(vishingScope);
    } catch {
      // The parent keeps this step open and provides the actionable error message.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveUser = (index) => {
    setVishingScope(prev => prev.filter((_, i) => i !== index));
  };

  const resetScenarioModal = () => {
    setShowModal(false);
    setEditingScenarioId(null);
    setScenarioData(null);
    setScenarioError('');
    setExtractionJsonText('{}');
    setExtractionJsonError('');
    setGoal('');
  };

  const closeScenarioModal = () => {
    if (isGenerating || isSaving) return;
    resetScenarioModal();
  };

  const openCreateScenario = () => {
    setEditingScenarioId(null);
    setScenarioData(null);
    setScenarioError('');
    setExtractionJsonText('{}');
    setExtractionJsonError('');
    setGoal('');
    setShowModal(true);
  };

  const openEditScenario = (scenario) => {
    const extractionJson = normalizeExtractionJson(scenario.extraction_json);
    setEditingScenarioId(scenario.id);
    setScenarioData({
      name: scenario.name || '',
      description: scenario.description || '',
      prompt: scenario.prompt || '',
      first_sentence: scenario.first_sentence || '',
      pass_fail_goal: scenario.pass_fail_goal || '',
      extraction_goal: scenario.extraction_goal || '',
      extraction_json: extractionJson
    });
    setExtractionJsonText(JSON.stringify(extractionJson, null, 2));
    setExtractionJsonError('');
    setScenarioError('');
    setGoal('');
    setShowModal(true);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setScenarioError('');
    try {
      const data = await generateCustomScenario(goal);
      const extractionJson = normalizeExtractionJson(data?.extraction_json);
      setScenarioData({
        name: data?.name || '',
        description: data?.description || '',
        prompt: data?.prompt || '',
        first_sentence: data?.first_sentence || '',
        pass_fail_goal: data?.pass_fail_goal || '',
        extraction_goal: data?.extraction_goal || '',
        extraction_json: extractionJson
      });
      setExtractionJsonText(JSON.stringify(extractionJson, null, 2));
    } catch (err) {
      setScenarioError(err?.message || 'Unable to generate the custom scenario.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    let extractionJson;
    try {
      extractionJson = JSON.parse(extractionJsonText || '{}');
      if (!extractionJson || typeof extractionJson !== 'object' || Array.isArray(extractionJson)) {
        throw new Error('Extraction JSON must be a JSON object.');
      }
    } catch (error) {
      setExtractionJsonError(error?.message || 'Enter a valid JSON object.');
      return;
    }

    setIsSaving(true);
    setScenarioError('');
    setExtractionJsonError('');
    try {
      const payload = {
        name: scenarioData.name,
        description: scenarioData.description,
        prompt: scenarioData.prompt,
        first_sentence: scenarioData.first_sentence,
        pass_fail_goal: scenarioData.pass_fail_goal,
        extraction_goal: scenarioData.extraction_goal,
        extraction_json: extractionJson
      };

      if (editingScenarioId != null) {
        const updatedScenario = await updateCustomScenario(editingScenarioId, payload);
        const nextScenarios = scenariosData.map((scenario) => (
          String(scenario.id) === String(editingScenarioId)
            ? { ...scenario, ...updatedScenario }
            : scenario
        ));
        syncScenarioList(nextScenarios);
        setVishingScope((currentUsers) => currentUsers.map((user) => (
          String(user.scenario) === String(editingScenarioId)
            ? {
                ...user,
                scenario_name: updatedScenario.name,
                scenario_description: updatedScenario.description
              }
            : user
        )));
      } else {
        await saveCustomScenario(payload);
        const { scenarios } = await fetchScenarios();
        syncScenarioList(scenarios);
      }
      resetScenarioModal();
    } catch (err) {
      setScenarioError(err?.message || 'Unable to save the custom scenario.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setScenarioData(prev => ({ ...prev, [field]: value }));
  };

  const openDeleteScenario = (scenario) => {
    setDeleteCandidate(scenario);
    setDeleteError('');
  };

  const closeDeleteScenario = () => {
    if (isDeleting) return;
    setDeleteCandidate(null);
    setDeleteError('');
  };

  const handleDeleteScenario = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      await deleteCustomScenario(deleteCandidate.id);
      const { scenarios: refreshedScenarios } = await fetchScenarios();
      const fallbackScenario = refreshedScenarios[0] || null;
      syncScenarioList(refreshedScenarios);
      setVishingScope((currentUsers) => currentUsers.map((user) => (
        String(user.scenario) === String(deleteCandidate.id)
          ? {
              ...user,
              scenario: fallbackScenario?.id ?? '',
              scenario_name: fallbackScenario?.name || '',
              scenario_description: fallbackScenario?.description || ''
            }
          : user
      )));
      setDeleteCandidate(null);
    } catch (error) {
      setDeleteError(error?.message || 'Unable to delete the custom scenario.');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderScenarioCarousel = () => (
    <SectionCard sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Scenarios & Voices</Typography>
          <Typography variant="body2" color="text.secondary">
            Browse vishing scenarios and voices. On the next section, assign one of each to every user.
          </Typography>
        </Box>
        <Pill label={tabIndex === 0 ? `Scenarios` : `Voices`} />
      </Stack>

      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 3 }}>
        <Tab label="Scenarios" />
        <Tab label="Voices" />
      </Tabs>

      {/* Carousel controls & items */}
      <CarouselWrap>
        <IconButton
          color="primary"
          onClick={() => tabIndex === 0 ? setScenarioPage(p => clamp(p - 1, 0, scenarioTotalPages - 1)) : setVoicePage(p => clamp(p - 1, 0, voiceTotalPages - 1))}
          disabled={(tabIndex === 0 && scenarioPage === 0) || (tabIndex === 1 && voicePage === 0)}
          aria-label="Previous"
        >
          <ChevronLeft />
        </IconButton>

        {tabIndex === 0
          ? (
            <>
              {visibleScenarioItems.map((item, idx) => (
                item === '__ADD__' ? (
                  <AddScenarioCard key={`add-${idx}`} onClick={openCreateScenario}>
                    <AddIcon />
                    <Typography fontWeight={700}>Add Scenario</Typography>
                  </AddScenarioCard>
                ) : (
                  <ItemCard key={item.id}>
                    {item.image && (
                      
                      <CardMedia
  component="video"
  src={item.video}
  alt={item.name}
  autoPlay
  muted
  loop
  playsInline
  sx={{
    width: "100%",
    height: "50%",
    objectFit: "cover",   // keeps aspect ratio, crops if needed
  }}
/>
                    )}
                    <CardContent sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={800} noWrap title={item.name}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.description || '—'}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 'auto', pt: 0.5 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={item.is_custom ? 'Team custom' : 'Prebuilt'}
                          sx={{ fontSize: 10, fontWeight: 700 }}
                        />
                        {(item.can_edit || item.can_delete) ? (
                          <Stack direction="row" spacing={0.5}>
                            {item.can_edit ? (
                              <Tooltip title={`Edit ${item.name}`}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label={`Edit ${item.name}`}
                                  onClick={() => openEditScenario(item)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {item.can_delete ? (
                              <Tooltip title={`Delete ${item.name}`}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Delete ${item.name}`}
                                  onClick={() => openDeleteScenario(item)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </ItemCard>
                )
              ))}
            </>
          ) : (
            <>
              {visibleVoiceItems.map((v) => (
                <ItemCard key={v.id}>
                  {v.image && (
                    <CardMedia
  component="video"
  src={v.video}
  alt={v.name}
  autoPlay
  muted
  loop
  playsInline
  sx={{
    width: "100%",
    height: "70%",
    objectFit: "cover",   // keeps aspect ratio, crops if needed
  }}
/>
                  )}
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800} noWrap title={v.name}>
                      {v.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {v.language || '—'}
                    </Typography>
                  </CardContent>
                </ItemCard>
              ))}
            </>
          )}

        <IconButton
          color="primary"
          onClick={() => tabIndex === 0 ? setScenarioPage(p => clamp(p + 1, 0, scenarioTotalPages - 1)) : setVoicePage(p => clamp(p + 1, 0, voiceTotalPages - 1))}
          disabled={(tabIndex === 0 && scenarioPage >= scenarioTotalPages - 1) || (tabIndex === 1 && voicePage >= voiceTotalPages - 1)}
          aria-label="Next"
        >
          <ChevronRight />
        </IconButton>
      </CarouselWrap>

      <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {tabIndex === 0 ? `Page ${scenarioPage + 1} of ${scenarioTotalPages}` : `Page ${voicePage + 1} of ${voiceTotalPages}`}
        </Typography>
      </Stack>
    </SectionCard>
  );

  const renderUsersAssignment = () => (
    <SectionCard>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Assign to Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a <strong>Scenario</strong>, <strong>Voice</strong>, caller number, and retry policy for each user.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Pill label={`Users: ${vishingScope.length}`} />
          <Pill label={callerNumbersLoading ? 'Loading caller numbers' : `${callerNumbers.length} caller numbers`} />
          <Button
            size="small"
            variant="outlined"
            startIcon={callerNumbersLoading ? <CircularProgress size={15} /> : <AutorenewIcon />}
            onClick={assignCallerNumbers}
            disabled={callerNumbersLoading || !callerNumbers.length}
          >
            Reassign automatic
          </Button>
        </Stack>
      </Stack>

      {campaignError ? (
        <Alert severity="error" sx={{ mb: 2 }} action={
          campaignError.includes('One or more caller numbers are unavailable for this team')
            ? <Button color="inherit" size="small" onClick={loadCallerNumbers}>Refresh numbers</Button>
            : null
        }>
          {campaignError}
        </Alert>
      ) : null}

      {callerNumbersError ? <Alert severity="warning" sx={{ mb: 2 }}>{callerNumbersError}</Alert> : null}

      <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 440 }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 1700 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 150 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 260 }}>Scenario</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 220 }}>Voice</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 220 }}>
                <Tooltip title="Additional calls after the initial attempt. Zero means one total attempt.">
                  <span>Maximum Redials</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: 330 }}>Caller Number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Bio</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 70 }} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vishingScope.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">No users added yet.</Typography>
                </TableCell>
              </TableRow>
            )}

            {vishingScope.map((u, i) => (
              <TableRow key={`${u.phone || 'row'}-${i}`} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: 'hidden' }}>
                    <Avatar sx={{ width: 28, height: 28 }}>{u.name?.[0]?.toUpperCase() || '?'}</Avatar>
                    <Typography variant="body2" noWrap title={u.name} sx={{ minWidth: 0 }}>
                      {u.name}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap title={u.phone}>
                    {u.phone}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Select
                    value={u.scenario ?? ''}
                    onChange={e => handleScenarioChange(i, e)}
                    size="small"
                    fullWidth
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Choose a scenario…</MenuItem>
                    {(scenariosData || []).map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                  {/* Keep extra description OFF the table to avoid stretching. */}
                </TableCell>

                <TableCell>
                  <Select
                    value={u.voice ?? ''}
                    onChange={e => handleVoiceChange(i, e)}
                    size="small"
                    fullWidth
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Choose a voice…</MenuItem>
                    {(voicesData || []).map(v => (
                      <MenuItem key={v.id} value={v.id}>{v.name}{v.language ? ` (${v.language})` : ''}</MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    value={normalizeMaxRedials(u.max_redials)}
                    onChange={(event) => handleMaxRedialsChange(i, event.target.value)}
                    size="small"
                    fullWidth
                    aria-label={`Maximum redials for ${u.name || 'target'}`}
                  >
                    {MAX_REDIAL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    value={u.caller_number || AUTOMATIC_CALLER_VALUE}
                    onChange={(event) => handleCallerNumberChange(i, event.target.value)}
                    size="small"
                    fullWidth
                    error={Boolean(u.caller_number && !callerNumbers.some((caller) => caller.phone_number === u.caller_number))}
                    disabled={callerNumbersLoading}
                    renderValue={(value) => {
                      if (value === AUTOMATIC_CALLER_VALUE) return 'Automatic round robin';
                      const selected = callerNumbers.find((caller) => caller.phone_number === value);
                      return selected
                        ? `${selected.label || 'Caller'} - ${formatCallerPhone(selected.phone_number)}`
                        : formatCallerPhone(value);
                    }}
                  >
                    <MenuItem value={AUTOMATIC_CALLER_VALUE}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Automatic round robin</Typography>
                        <Typography variant="caption" color="text.secondary">Let the calling service choose an available number.</Typography>
                      </Box>
                    </MenuItem>
                    {u.caller_number && !callerNumbers.some((caller) => caller.phone_number === u.caller_number) ? (
                      <MenuItem value={u.caller_number} disabled>
                        Unavailable - {formatCallerPhone(u.caller_number)}
                      </MenuItem>
                    ) : null}
                    {(callerNumbers || []).map((caller) => {
                      const countryCode = getCallerCountryCode(caller);
                      return (
                        <MenuItem key={caller.phone_number} value={caller.phone_number} sx={{ py: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Typography aria-hidden="true">{getCountryFlag(countryCode) || 'GL'}</Typography>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {caller.label || 'Caller number'} - {formatCallerPhone(caller.phone_number)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {[caller.city, caller.country].filter(Boolean).join(', ') || 'Location unavailable'}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={caller.is_team_specific ? 'Team' : 'Global'}
                              color={caller.is_team_specific ? 'primary' : 'default'}
                              variant={caller.is_team_specific ? 'filled' : 'outlined'}
                            />
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </TableCell>

                <TableCell>
                  <Tooltip title={u.person_bio || ''} placement="top" arrow>
                    <span style={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>
                      {u.person_bio || '—'}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell align="right">
                  <IconButton color="error" aria-label="Remove user" onClick={() => handleRemoveUser(i)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
        <Button onClick={onBack}>Back</Button>
        <Button variant="contained" onClick={handleCampaignSave} disabled={vishingScope.length === 0 || isSubmitting}>
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Save & Continue'}
        </Button>
      </Stack>
    </SectionCard>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1150, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Stack spacing={3}>
        {renderScenarioCarousel()}

        {renderUsersAssignment()}
      </Stack>

      {/* Scenario Generator Modal */}
      <Dialog open={showModal} onClose={closeScenarioModal} fullWidth maxWidth="md">
        <DialogTitle>
          {editingScenarioId != null
            ? 'Edit Custom Scenario'
            : scenarioData
              ? 'Review & Edit Scenario'
              : 'Generate Custom Scenario'}
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {scenarioError ? <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{scenarioError}</Alert> : null}
          {!scenarioData ? (
            <TextField
              fullWidth
              label="Scenario Goal"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              multiline
              minRows={6}
              placeholder="e.g., Impersonate IT desk to obtain a one-time passcode from finance staff…"
              sx={{ mt: 2 }}
            />
          ) : (
            <>
              <TextField required label="Name" fullWidth value={scenarioData.name || ''} onChange={e => handleInputChange('name', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Description" fullWidth multiline minRows={3} value={scenarioData.description || ''} onChange={e => handleInputChange('description', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Prompt" fullWidth multiline minRows={5} value={scenarioData.prompt || ''} onChange={e => handleInputChange('prompt', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="First Sentence" fullWidth multiline minRows={2} value={scenarioData.first_sentence || ''} onChange={e => handleInputChange('first_sentence', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Pass/Fail Goal" fullWidth multiline minRows={2} value={scenarioData.pass_fail_goal || ''} onChange={e => handleInputChange('pass_fail_goal', e.target.value)} sx={{ mt: 2 }} />
              <TextField label="Extraction Goal" fullWidth multiline minRows={2} value={scenarioData.extraction_goal || ''} onChange={e => handleInputChange('extraction_goal', e.target.value)} sx={{ mt: 2 }} />
              <TextField
                label="Extraction JSON"
                fullWidth
                multiline
                minRows={5}
                value={extractionJsonText}
                error={Boolean(extractionJsonError)}
                helperText={extractionJsonError || 'Enter a JSON object describing the structured values to extract.'}
                onChange={e => {
                  setExtractionJsonText(e.target.value);
                  setExtractionJsonError('');
                }}
                inputProps={{ spellCheck: false }}
                sx={{ mt: 2, '& textarea': { fontFamily: 'monospace' } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScenarioModal} disabled={isGenerating || isSaving}>Cancel</Button>
          {!scenarioData ? (
            <Button onClick={handleGenerate} disabled={!goal || isGenerating}>
              {isGenerating ? <CircularProgress size={20} /> : 'Generate'}
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={isSaving || !scenarioData.name?.trim()}>
              {isSaving
                ? <CircularProgress size={20} />
                : editingScenarioId != null
                  ? 'Update Scenario'
                  : 'Save Scenario'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteCandidate)} onClose={closeDeleteScenario} fullWidth maxWidth="xs">
        <DialogTitle>Delete custom scenario?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Delete <strong>{deleteCandidate?.name}</strong>? This removes the scenario for your team and cannot be undone.
          </Typography>
          {deleteError ? <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteScenario} disabled={isDeleting}>Keep Scenario</Button>
          <Button color="error" variant="contained" onClick={handleDeleteScenario} disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Delete Scenario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VishingStep3;
